import React, { useEffect } from 'react';
import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    PDFViewer,
    Font
} from '@react-pdf/renderer';
import { Button, Box } from '@mui/material';
import { useFormContext } from '../context/FormContext';
import { goalMappings } from '../utils/consts';

// Register Roboto font with simplified configuration
Font.register({
    family: 'Roboto',
    src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf'
});

// Register bold and italic fonts separately
Font.register({
    family: 'Roboto-Bold',
    src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf'
});

Font.register({
    family: 'Roboto-Italic',
    src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-italic-webfont.ttf'
});

// Create styles
const styles = StyleSheet.create({
    page: {
        padding: 30,
        backgroundColor: '#FFFFFF',
        fontFamily: 'Roboto'
    },
    section: {
        marginBottom: 30,
        borderBottom: '1 solid #CCCCCC',
        paddingBottom: 20
    },
    title: {
        fontSize: 24,
        marginBottom: 20,
        textAlign: 'center',
        fontFamily: 'Roboto-Bold'
    },
    introText: {
        fontSize: 12,
        marginBottom: 30,
        lineHeight: 1.5,
        textAlign: 'justify',
        fontFamily: 'Roboto'
    },
    sectionTitle: {
        fontSize: 16,
        marginBottom: 15,
        fontFamily: 'Roboto-Bold',
        color: '#000000'
    },
    explanatoryText: {
        fontSize: 12,
        marginBottom: 10,
        color: '#2196f3',
        lineHeight: 1.5,
        fontFamily: 'Roboto-Italic'
    },
    content: {
        fontSize: 12,
        marginBottom: 10,
        lineHeight: 1.5,
        fontFamily: 'Roboto'
    },
    highlightedContent: {
        fontSize: 12,
        marginBottom: 10,
        color: '#4CAF50',
        fontFamily: 'Roboto-Bold'
    },
    bulletPoint: {
        marginLeft: 10,
        marginBottom: 5,
        fontFamily: 'Roboto'
    },
    numberedPoint: {
        marginLeft: 10,
        marginBottom: 5,
        fontFamily: 'Roboto'
    },
    firstStep: {
        fontSize: 12,
        marginTop: 10,
        marginBottom: 10,
        color: '#4CAF50',
        fontFamily: 'Roboto-Bold',
        paddingTop: 10
    },
    rankedValue: {
        fontSize: 12,
        marginBottom: 8,
        color: '#4CAF50',
        fontFamily: 'Roboto-Bold',
        marginLeft: 10
    }
});

const formatList = (list = [], numbered = false, ranked = false) => {
    if (!Array.isArray(list)) return '';
    if (ranked) {
        // For values, we reverse the array to show them in order of importance
        return list.slice().reverse().map((item, index) =>
            `${index + 1}. ${item} ${index === 0 ? '(najważniejsza)' :
                index === list.length - 1 ? '(wspierająca)' : ''}`
        ).join('\n');
    }
    return list.map((item, index) =>
        `${numbered ? (index + 1) + '. ' : '• '}${item}`
    ).join('\n');
};

const PDFDocument = ({ formData }) => {
    const processGoal = goalMappings[formData.cel_wybor]?.process || formData.cel_wybor;

    const introText = `Oto Twój "PDF Mocy". Jest to plik, który może Ci pomóc w osiągnięciu celu, zawiera on esencję tego co dla Ciebie ważne, czym się kierujesz oraz jakie masz możliwości by osiągnąć cel jakim jest ${processGoal}. Największy efekt przyniesie gdy będzie blisko Ciebie - zawieś go na ścianie w miejscu w którym przygotowujesz się do zmiany pracy (np. nad biurkiem), noś go w kieszeni i zaglądaj do niego tak często, jak to możliwe. Niech będzie największym wsparciem i drogowskazem dla Ciebie w czasie zmiany.`;

    return (
        <PDFViewer style={{ width: '100%', height: '100%' }}>

            <Document>
                <Page size="A4" style={styles.page}>
                    <Text style={styles.title}>PDF Mocy</Text>
                    <Text style={styles.introText}>{introText}</Text>

                    {/* Cel Section */}
                    <View>
                        <Text style={styles.sectionTitle}>Chcę osiągnąć...</Text>
                        <Text style={styles.explanatoryText}>
                            Celem jest to, co byś chciał żeby się stało, a co jest możliwe przy podjęciu określonych działań.
                            Cel to marzenie wyposażone w strategię, kolejne kroki. To konkretny punkt, który chcesz osiągnąć w określonej przyszłości.
                        </Text>
                        <Text style={styles.explanatoryText}>
                            Wyznaczone cele zwiększają motywację, ułatwiają planowanie, dają powód do działania i pomagają utrzymać zaangażowanie.
                            Dają poczucie kontroli i budują pewność siebie. Dlatego doskonale, że go jasno określiłeś.
                        </Text>
                        <Text style={styles.content}>Twoim celem jest...</Text>
                        <Text style={styles.highlightedContent}>{processGoal}.</Text>
                    </View>
                    <View>
                        <Text style={styles.sectionTitle}>Potrzeby</Text>
                        <Text style={styles.explanatoryText}>
                            Pamiętaj! To dzięki określeniu potrzeb będziesz wiedział czy cel, który wymyśliłeś, będzie w stanie je zaspokoić.
                            Tylko zaspokojone potrzeby dają szansę na długotrwałą satysfakcję z osiągniętego celu.
                        </Text>
                        <Text style={styles.content}>
                            Chcę aby {processGoal} zaspokajało takie potrzeby jak:
                        </Text>
                        <Text style={styles.highlightedContent}>{formatList(formData.potrzeby_wybrane)}</Text>
                    </View>

                    {/* Wartości Section */}
                    <View>
                        <Text style={styles.sectionTitle}>Wartości</Text>
                        <Text style={styles.explanatoryText}>
                            Wartości są drugim niezbędnym elementem, którego realizacja daje szansę na długotrwałą satysfakcję.
                            Razem z potrzebami stanowią podstawę długotrwałego poczucia szczęścia z realizowanych zamierzeń.
                            Świadomość hierarchii wartości pomaga w podejmowaniu decyzji zgodnych z tym, co jest dla Ciebie najważniejsze.
                        </Text>
                        <Text style={styles.content}>Chcę aby {processGoal} wspierało realizację takich wartości jak (od najważniejszej do wspierającej):</Text>
                        <Text style={styles.rankedValue}>{formatList([...formData.wartosci_wybrane_poczatek, ...formData.hierarchia_wartosci], false, true)}</Text>
                    </View>

                    {/* Zasoby Section */}
                    <View>
                        <Text style={styles.sectionTitle}>Zasoby </Text>
                        <Text style={styles.content}>Co już mam?</Text>
                        <Text style={styles.explanatoryText}>
                            Przy realizacji tego celu, zasoby to wszystko, co możesz wykorzystać w {processGoal.replace('enie', 'eniu').replace('wrót', 'wrocie').replace('zja', 'zji').replace('anie', 'aniu')}.Świadomość zasobów jest drogą do efektywnego ich wykorzystania dla osiągnięcia celu.
                        </Text>
                        <Text style={styles.highlightedContent}>{formatList(formData.zasoby_lista)}</Text>
                        <Text style={styles.content}>Czego potrzebuję, a jeszcze nie mam?</Text>
                        <Text style={styles.explanatoryText}>
                            Świadomość zasobów, których Ci jeszcze brakuje, daje szansę na ich uzupełnienie.
                        </Text>
                        <Text style={styles.highlightedContent}>{formData.zasoby_brakujace}</Text>
                    </View>

                    {/* Wspierające Myśli Section */}
                    <View>
                        <Text style={styles.sectionTitle}>Wspierające myśli</Text>
                        <Text style={styles.explanatoryText}>
                            Wyłapanie wspierających myśli ma znaczenie w utrzymaniu motywacji i radzeniu sobie z trudnościami w trakcie realizacji celu.
                            Wspierające myśli pozwalają wierzyć, że cel jest możliwy, dodają energii i motywują do działania.
                        </Text>
                        <Text style={styles.content}>Oto myśli, które wspierają mój cel:</Text>
                        <Text style={styles.highlightedContent}>{formatList(formData.wspierajace_mysli_lista)}</Text>
                    </View>

                    {/* Ekologia Section */}
                    <View>
                        <Text style={styles.sectionTitle}>Przewidywany wpływ celu na otoczenie</Text>
                        <Text style={styles.explanatoryText}>
                            {String(processGoal).charAt(0).toUpperCase() + String(processGoal).slice(1)} będzie miało wpływ na Twoje otoczenie. Świadomość tego wpływu może być dla Ciebie dodatkową motywacją do działania.
                        </Text>
                        <Text style={styles.content}>Przewiduje, że {processGoal} będzie miał taki wpływ na moje otoczenie:</Text>
                        <Text style={styles.highlightedContent}>{formData.ekologia_codziennosc}</Text>
                        <Text style={styles.highlightedContent}>{formData.ekologia_relacje}</Text>
                        <Text style={styles.highlightedContent}>{formData.ekologia_mysli_o_sobie}</Text>
                        <Text style={styles.highlightedContent}>{formData.ekologia_samopoczucie}</Text>
                    </View>

                    {/* Plan Działania Section */}
                    <View>
                        <Text style={styles.sectionTitle}>Plan działania</Text>
                        <Text style={styles.explanatoryText}>Plan działania i strategia są kluczowe dla skutecznej realizacji celu. Działają one jak mapa i kompas, pomagając utrzymać kurs
                            i unikać zbędnych przeszkód</Text>
                        <Text style={styles.content}>Oto kroki, które pomogą mi osiągnąć cel:</Text>
                        <Text style={styles.highlightedContent}>{formatList(formData.plan_dzialania_kroki, true)}</Text>
                        {formData.plan_dzialania_kroki?.length > 0 && (
                            <>
                                <Text style={styles.content}>Oto mój pierwszy mały krok:</Text>
                                <Text style={styles.firstStep}>{formData.plan_dzialania_kroki[0]}</Text>
                            </>
                        )}
                    </View>
                </Page>
            </Document>
        </PDFViewer>
    );
};

const PDFGenerator = ({ formData }) => {
    const { resetForm } = useFormContext();

    return (
        <Box>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={() => {
                    window.location.reload();
                }}>
                    Pdf się nie pojawił? Naciśnij mnie!
                </Button>
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={resetForm}
                    sx={{ mb: 2 }}
                >
                    Zacznij od nowa
                </Button>
            </Box>
            <Box sx={{ width: '100%', height: 'calc(100vh - 80px)' }}>
                <PDFDocument formData={formData} />

            </Box>
        </Box>
    );
};

export default PDFGenerator;